import { IFilter } from "@saleor/components/Filter";
import { AutocompleteFilterOpts, FilterOpts, MinMax } from "@saleor/types";
import {
  createAutocompleteField,
  createDateField,
  createOptionsField
} from "@saleor/utils/filters/fields";
import { defineMessages, IntlShape } from "react-intl";

export enum GrabExpressFilterKeys {
  completedAt = "completedAt",
  createdDate = "createdDate",
  mall = "mall",
  orderStatus = "orderStatus",
  shippingMethodName = "shippingMethodName"
}

export enum orderStatus {
  acknowledged = "acknowledged",
  canceled = "canceled",
  collected = "collected",
  completed = "completed",
  newOrder = "new order",
  packed = "packed",
  ready = "ready",
  receivedAtFc = "received at fc",
  readyToShip = "ready to ship",
  shipped = "shipped"
}

export enum deliveryType {
  EXPRESS = "express",
  STANDARD_DELIVERY = "next_day",
  SAME_DAY_DELIVERY = "same_day"
}

export interface GrabExpressListFilterOpts {
  mall: FilterOpts<string[]> & AutocompleteFilterOpts;
  completedAt: FilterOpts<MinMax>;
  createdDate: FilterOpts<MinMax>;
  orderStatus: FilterOpts<string[]>;
  shippingMethodName: FilterOpts<string[]>;
}

const messages = defineMessages({
  acknowledged: {
    defaultMessage: "ACKNOWLEDGED",
    description: "acknowledged"
  },
  canceled: {
    defaultMessage: "CANCELED",
    description: "canceled status"
  },
  collected: {
    defaultMessage: "COLLECTED",
    description: "collected status"
  },
  completed: {
    defaultMessage: "COMPLETED",
    description: "completed status"
  },
  completedAt: {
    defaultMessage: "Order Completion Date",
    description: "order completion date"
  },
  created: {
    defaultMessage: "Order Created Date",
    description: "order created date"
  },
  express: {
    defaultMessage: "Express",
    description: "express"
  },
  inStorePickup: {
    defaultMessage: "In Store Pickup ",
    description: "in store pickup "
  },
  mall: {
    defaultMessage: "Mall",
    description: "mall name"
  },
  newOrder: {
    defaultMessage: "NEW ORDER",
    description: "new order"
  },
  orderStatus: {
    defaultMessage: "Order Status",
    description: "order status"
  },
  packed: {
    defaultMessage: "PACKED",
    description: "packed status"
  },
  ready: {
    defaultMessage: "READY",
    description: "ready"
  },
  readyToShip: {
    defaultMessage: "READY TO SHIP",
    description: "ready to ship status"
  },
  receivedAtFc: {
    defaultMessage: "RECEIVED AT FC",
    description: "received at fc status"
  },
  sameDayDelivery: {
    defaultMessage: "Same Day Delivery",
    description: "same day delivery"
  },
  shipped: {
    defaultMessage: "SHIPPED",
    description: "shipped status"
  },
  shippingMethodName: {
    defaultMessage: "Delivery Type",
    description: "delivery type"
  },
  standardDelivery: {
    defaultMessage: "Standard Delivery",
    description: "standard delivery"
  }
});

export function createFilterStructure(
  intl: IntlShape,
  opts: GrabExpressListFilterOpts
): IFilter<GrabExpressFilterKeys> {
  return [
    {
      ...createAutocompleteField(
        GrabExpressFilterKeys.mall,
        intl.formatMessage(messages.mall),
        opts.mall.value,
        opts.mall.displayValues,
        true,
        opts.mall.choices,
        {
          hasMore: opts.mall.hasMore,
          initialSearch: "",
          loading: opts.mall.loading,
          onFetchMore: opts.mall.onFetchMore,
          onSearchChange: opts.mall.onSearchChange
        }
      ),
      active: opts.mall.active
    },
    {
      ...createDateField(
        GrabExpressFilterKeys.createdDate,
        intl.formatMessage(messages.created),
        opts.createdDate.value
      ),
      active: opts.createdDate.active
    },
    {
      ...createOptionsField(
        GrabExpressFilterKeys.orderStatus,
        intl.formatMessage(messages.orderStatus),
        opts.orderStatus.value,
        true,
        [
          {
            label: intl.formatMessage(messages.newOrder),
            value: orderStatus.newOrder
          },
          {
            label: intl.formatMessage(messages.acknowledged),
            value: orderStatus.acknowledged
          },
          {
            label: intl.formatMessage(messages.ready),
            value: orderStatus.ready
          },
          {
            label: intl.formatMessage(messages.collected),
            value: orderStatus.collected
          },
          {
            label: intl.formatMessage(messages.receivedAtFc),
            value: orderStatus.receivedAtFc
          },
          {
            label: intl.formatMessage(messages.packed),
            value: orderStatus.packed
          },
          {
            label: intl.formatMessage(messages.readyToShip),
            value: orderStatus.readyToShip
          },
          {
            label: intl.formatMessage(messages.shipped),
            value: orderStatus.shipped
          },
          {
            label: intl.formatMessage(messages.completed),
            value: orderStatus.completed
          },
          {
            label: intl.formatMessage(messages.canceled),
            value: orderStatus.canceled
          }
        ]
      ),
      active: opts.orderStatus.active
    },
    {
      ...createOptionsField(
        GrabExpressFilterKeys.shippingMethodName,
        intl.formatMessage(messages.shippingMethodName),
        opts.shippingMethodName.value,
        true,
        [
          {
            label: intl.formatMessage(messages.express),
            value: deliveryType.EXPRESS
          },
          {
            label: intl.formatMessage(messages.standardDelivery),
            value: deliveryType.STANDARD_DELIVERY
          },
          {
            label: intl.formatMessage(messages.sameDayDelivery),
            value: deliveryType.SAME_DAY_DELIVERY
          }
        ]
      ),
      active: opts.shippingMethodName.active
    },
    {
      ...createDateField(
        GrabExpressFilterKeys.completedAt,
        intl.formatMessage(messages.completedAt),
        opts.completedAt.value
      ),
      active: opts.completedAt.active
    }
  ];
}
